// Chat wire schemas. Shared by SDK (client-side validation), API (Fastify
// type-provider), and dashboard. The SSE event union is the *only* thing that
// may cross the wire on /v1/chat/stream — any new variant lands here first.
import { Type } from '@sinclair/typebox';
import { ProviderSchema, FinishReasonSchema } from './providers.js';
// ---------- Messages ----------
export const ChatRoleSchema = Type.Union([Type.Literal('system'), Type.Literal('user'), Type.Literal('assistant'), Type.Literal('tool')], { $id: 'ChatRole' });
// Tool-call shape matches provider-agnostic form: name + JSON args.
export const ToolCallSchema = Type.Object({
    id: Type.String({ minLength: 1, maxLength: 128 }),
    name: Type.String({ minLength: 1, maxLength: 128 }),
    arguments: Type.Record(Type.String(), Type.Unknown()),
}, { $id: 'ToolCall', additionalProperties: false });
export const ChatMessageSchema = Type.Object({
    role: ChatRoleSchema,
    // 32k chars ≈ 8k tokens at the heuristic 4 chars/token. Aligns with
    // the prevailing per-message ceiling across Anthropic / OpenAI APIs and
    // keeps a single turn from exhausting small plan caps (Free: 50k tokens
    // /period). Was 200_000 (~50k tokens) — too generous; one abusive turn
    // could detonate a Free tenant's monthly cap.
    content: Type.String({ maxLength: 32_000 }),
    // Present only on assistant turns that invoked tools.
    toolCalls: Type.Optional(Type.Array(ToolCallSchema, { maxItems: 16 })),
    // Present only on role='tool' turns — references the assistant's toolCalls[].id.
    toolCallId: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
    name: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
}, { $id: 'ChatMessage', additionalProperties: false });
// ---------- Request ----------
export const ChatRequestSchema = Type.Object({
    agentId: Type.String({ format: 'uuid' }),
    sessionToken: Type.String({ minLength: 16, maxLength: 256 }),
    messages: Type.Array(ChatMessageSchema, { minItems: 1, maxItems: 64 }),
    // Optional overrides — server validates against agent policy.
    model: Type.Optional(Type.String({ minLength: 1, maxLength: 128 })),
    provider: Type.Optional(ProviderSchema),
    temperature: Type.Optional(Type.Number({ minimum: 0, maximum: 2 })),
    maxOutputTokens: Type.Optional(Type.Integer({ minimum: 1, maximum: 16_384 })),
    // Opaque idempotency token (client-generated, UUID-ish).
    requestId: Type.Optional(Type.String({ minLength: 8, maxLength: 128 })),
    // Resumes an existing conversation; omit to open a new one. Server rejects
    // ids that don't belong to the caller's tenant (RLS handles it silently).
    conversationId: Type.Optional(Type.String({ format: 'uuid' })),
}, { $id: 'ChatRequest', additionalProperties: false });
// ---------- SSE events ----------
// Event names map 1:1 to `event:` lines. Data is the JSON `data:` payload.
// Order guarantee: `start` first, `done` or `error` last. Exactly one terminator.
export const ChatEventStartSchema = Type.Object({
    type: Type.Literal('start'),
    requestId: Type.String({ minLength: 8, maxLength: 128 }),
    runId: Type.String({ format: 'uuid' }),
    provider: ProviderSchema,
    model: Type.String({ minLength: 1, maxLength: 128 }),
    // Echoed on every turn so the client can persist/resume without a second round-trip.
    conversationId: Type.String({ format: 'uuid' }),
}, { additionalProperties: false });
export const ChatEventDeltaSchema = Type.Object({
    type: Type.Literal('delta'),
    text: Type.String({ maxLength: 16_384 }),
}, { additionalProperties: false });
export const ChatEventToolCallSchema = Type.Object({
    type: Type.Literal('tool_call'),
    call: ToolCallSchema,
}, { additionalProperties: false });
export const ChatEventToolResultSchema = Type.Object({
    type: Type.Literal('tool_result'),
    toolCallId: Type.String({ minLength: 1, maxLength: 128 }),
    // Stringified — tools return JSON, but SSE frames want opaque text.
    result: Type.String({ maxLength: 64_000 }),
    isError: Type.Optional(Type.Boolean()),
}, { additionalProperties: false });
export const ChatEventUsageSchema = Type.Object({
    type: Type.Literal('usage'),
    inputTokens: Type.Integer({ minimum: 0 }),
    outputTokens: Type.Integer({ minimum: 0 }),
    costUsd: Type.Number({ minimum: 0 }),
}, { additionalProperties: false });
export const ChatEventDoneSchema = Type.Object({
    type: Type.Literal('done'),
    finishReason: FinishReasonSchema,
}, { additionalProperties: false });
export const ChatEventErrorSchema = Type.Object({
    type: Type.Literal('error'),
    code: Type.String({ minLength: 1, maxLength: 64 }),
    message: Type.String({ minLength: 1, maxLength: 2_000 }),
    http: Type.Integer({ minimum: 400, maximum: 599 }),
}, { additionalProperties: false });
export const ChatEventSchema = Type.Union([
    ChatEventStartSchema,
    ChatEventDeltaSchema,
    ChatEventToolCallSchema,
    ChatEventToolResultSchema,
    ChatEventUsageSchema,
    ChatEventDoneSchema,
    ChatEventErrorSchema,
], { $id: 'ChatEvent' });
//# sourceMappingURL=chat.js.map